// AI Service for handling LLM interactions
class AIService {
  constructor() {
    this.provider = process.env.REACT_APP_AI_PROVIDER || 'openai';
    this.openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.openaiModel = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';
    this.openaiBaseUrl = process.env.REACT_APP_OPENAI_BASE_URL || 'https://api.openai.com/v1';
    this.ollamaBaseUrl = process.env.REACT_APP_OLLAMA_BASE_URL || 'http://localhost:11434';
    this.ollamaModel = process.env.REACT_APP_OLLAMA_MODEL || 'llama3.2';
  }

  /**
   * Исправляет грамматические, орфографические и пунктуационные ошибки в тексте
   */
  async fixGrammarErrors(text, language = 'ru') {
    if (!text || text.length === 0) return text;
    
    try {
      if (this.provider === 'openai' && this.openaiApiKey && this.openaiApiKey !== 'your_openai_api_key_here') {
        const response = await fetch(`${this.openaiBaseUrl}/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.openaiApiKey}`
          },
          body: JSON.stringify({
            model: this.openaiModel,
            messages: [
              {
                role: 'system',
                content: 'Ты помощник для исправления грамматических, орфографических и пунктуационных ошибок в русском языке. Исправь текст, сохранив его смысл и стиль. Верни ТОЛЬКО исправленный текст, без дополнительных комментариев.'
              },
              {
                role: 'user',
                content: `Исправь грамматические, орфографические и пунктуационные ошибки в следующем тексте. Сохрани смысл и стиль. Верни только исправленный текст:\n\n"${text}"`
              }
            ],
            temperature: 0.3,
            max_tokens: text.length + 100
          })
        });

        if (response.ok) {
          const data = await response.json();
          const correctedText = data.choices[0].message.content.trim();
          // Убираем кавычки, если они есть
          return correctedText.replace(/^["']|["']$/g, '').trim();
        }
      }
    } catch (error) {
      console.error('Error fixing grammar:', error);
    }
    
    // Fallback - возвращаем оригинальный текст
    return text;
  }

  async generateTitleFromText(text, language = 'ru') {
    
    try {
      // Сначала исправляем грамматические ошибки в тексте
      const correctedText = await this.fixGrammarErrors(text, language);
      
      if (this.provider === 'openai') {
        return await this.generateTitleWithOpenAI(correctedText, language);
      } else if (this.provider === 'ollama') {
        return await this.generateTitleWithOllama(correctedText, language);
      } else {
        throw new Error('Неизвестный AI провайдер');
      }
    } catch (error) {
      console.error('AI Service Error generating title:', error);
      // Fallback - извлекаем первые ключевые слова из текста (максимум 8 слов)
      const words = text.split(/\s+/).slice(0, 8);
      const fallbackTitle = words.join(' ').substring(0, 60).trim() || 'Петиция';
      return fallbackTitle;
    }
  }

  async generateTitleWithOpenAI(text, language) {
    if (!this.openaiApiKey || this.openaiApiKey === 'your_openai_api_key_here') {
      throw new Error('OpenAI API ключ не настроен');
    }

    const prompt = language === 'ru' 
      ? `Проанализируй текст петиции и создай заголовок на русском языке, который точно передаёт основную идею и смысл обращения.\n\nТекст: "${text}"\n\nКРИТИЧЕСКИ ВАЖНО:\n1. Заголовок должен быть ЕСТЕСТВЕННЫМ, как на сайте с петициями - БЕЗ фраз вроде "Создать петицию", "Мы требуем", "Прошу", "Петиция о..."\n2. Прочитай ВЕСЬ текст полностью, определи СУТЬ/ОСНОВНУЮ ИДЕЮ\n3. Определи позицию автора: ЗА что-то или ПРОТИВ чего-то\n\nПРАВИЛА ФОРМУЛИРОВКИ:\n- Если по смыслу человек ПРОТИВ чего-то → заголовок должен звучать как протест:\n  * Используй глаголы: "Прекратить...", "Отменить...", "Запретить...", "Остановить..."\n  * Пример: "Прекратить незаконную вырубку леса"\n\n- Если по смыслу человек ЗА что-то → заголовок должен быть положительным:\n  * Используй глаголы: "Разрешить...", "Поддержать...", "Создать...", "Вернуть...", "Установить..."\n  * Пример: "Вернуть вывоз мусора в районе"\n\nТРЕБОВАНИЯ:\n- Оптимальная длина для понимания сути проблемы\n- Естественный заголовок, как на реальном сайте петиций\n- Отражает ГЛАВНУЮ ЦЕЛЬ/СУТЬ из всего текста\n- Без формальных фраз типа "Создать петицию", "Прошу рассмотреть"\n- Без конкретных адресов, имен, цифр (но можно общую тему)\n\nПРИМЕРЫ ПРАВИЛЬНЫХ ЗАГОЛОВКОВ:\n- Текст: "В нашем районе перестали вывозить мусор. Жители требуют вернуть регулярный вывоз отходов."\n  → "Вернуть вывоз мусора в районе"\n  → "Регулярный вывоз мусора для жителей района"\n\n- Текст: "Нужно отремонтировать дорогу на улице Ленина"\n  → "Ремонт дороги на улице Ленина"\n  → "Отремонтировать дорогу"\n\n- Текст: "Против незаконной вырубки деревьев в парке"\n  → "Прекратить незаконную вырубку деревьев"\n  → "Запретить вырубку в парке"\n\nСоздай ЕСТЕСТВЕННЫЙ заголовок (до 8 слов) на основе ГЛАВНОЙ ИДЕИ из всего текста:`
      : `READ THE ENTIRE TEXT BELOW AND ANALYZE THE MAIN MESSAGE/IDEA THE AUTHOR WANTS TO CONVEY.\n\nText: "${text}"\n\nCRITICALLY IMPORTANT:\n1. IGNORE formal phrases at the beginning like "I want to create a petition about", "I need", "I request" - these are NOT part of the meaning\n2. Read the ENTIRE text completely, including the middle and end\n3. Determine the ESSENCE of the problem or request - what does the author really want?\n4. Create a title based on the ESSENCE, NOT on the first words\n\nExamples:\n- Text: "I want to create a petition about garbage collection on certain days of the week based on the city district"\n  Correct title: "Garbage Collection Schedule" (essence - organizing garbage collection by days)\n  WRONG: "I want to create a petition" (these are the first words, not the essence)\n\n- Text: "Please repair the road on Lenin Street, house 5, there are many potholes"\n  Correct title: "Road Repair" (essence - need repair)\n  WRONG: "Please repair the road" (these are the first words)\n\nTitle requirements:\n- 3-8 words, maximum 60 characters\n- Reflects the ESSENCE/MAIN IDEA of the problem, not formal words at the beginning\n- Without details, addresses, names, numbers\n- Examples of good titles: "Garbage Collection Schedule", "Road Repair", "New School", "Park Cleanup"\n\nCreate a title based on the ESSENCE (not first words!):`;

    const response = await fetch(`${this.openaiBaseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.openaiApiKey}`
      },
      body: JSON.stringify({
        model: this.openaiModel,
        messages: [
          {
            role: 'system',
            content: 'Ты помощник для создания заголовков петиций на русском языке. Создавай ЕСТЕСТВЕННЫЕ заголовки (до 8 слов), как на сайте с петициями. ВАЖНО:\n1. ИГНОРИРУЙ формальные фразы: "Создать петицию", "Мы требуем", "Прошу", "Петиция о..." - это НЕ часть заголовка\n2. Читай ВЕСЬ текст полностью, определяй СУТЬ/ОСНОВНУЮ ИДЕЮ\n3. Определяй позицию автора: ЗА что-то или ПРОТИВ\n4. Если ПРОТИВ - используй глаголы: "Прекратить", "Отменить", "Запретить", "Остановить"\n5. Если ЗА - используй глаголы: "Разрешить", "Поддержать", "Создать", "Вернуть", "Установить"\n6. Заголовок должен быть ЕСТЕСТВЕННЫМ, как на реальном сайте петиций\n7. Оптимальная длина для понимания сути\n8. Если в тексте несколько тем (через "и", "а также"), выбери ОДНУ ГЛАВНУЮ тему для заголовка\nПримеры: "Вернуть вывоз мусора в районе", "Регулярный вывоз мусора для жителей", "Прекратить незаконную вырубку леса", "Запретить отстрел собак"'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 80
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Ошибка OpenAI API');
    }

    const data = await response.json();
    let title = data.choices[0].message.content.trim();
    
    // Очищаем заголовок от формальных фраз
    title = this.cleanTitleFromFormalPhrases(title, text);
    
    // Обрезаем до 60 символов, но по словам (не по середине слова)
    if (title.length > 60) {
      title = title.substring(0, 60).trim();
      const lastSpace = title.lastIndexOf(' ');
      if (lastSpace > 20) {
        title = title.substring(0, lastSpace).trim();
      }
    }
    
    return title;
  }

  async generateTitleWithOllama(text, language) {
    const prompt = language === 'ru' 
      ? `Проанализируй текст петиции и создай заголовок на русском языке, который точно передаёт основную идею и смысл обращения.\n\nТекст: "${text}"\n\nКРИТИЧЕСКИ ВАЖНО:\n1. Заголовок должен быть ЕСТЕСТВЕННЫМ, как на сайте с петициями - БЕЗ фраз вроде "Создать петицию", "Мы требуем", "Прошу", "Петиция о..."\n2. Прочитай ВЕСЬ текст полностью, определи СУТЬ/ОСНОВНУЮ ИДЕЮ\n3. Определи позицию автора: ЗА что-то или ПРОТИВ чего-то\n\nПРАВИЛА ФОРМУЛИРОВКИ:\n- Если по смыслу человек ПРОТИВ чего-то → заголовок должен звучать как протест:\n  * Используй глаголы: "Прекратить...", "Отменить...", "Запретить...", "Остановить..."\n  * Пример: "Прекратить незаконную вырубку леса"\n\n- Если по смыслу человек ЗА что-то → заголовок должен быть положительным:\n  * Используй глаголы: "Разрешить...", "Поддержать...", "Создать...", "Вернуть...", "Установить..."\n  * Пример: "Вернуть вывоз мусора в районе"\n\nТРЕБОВАНИЯ:\n- Оптимальная длина для понимания сути проблемы\n- Естественный заголовок, как на реальном сайте петиций\n- Отражает ГЛАВНУЮ ЦЕЛЬ/СУТЬ из всего текста\n- Без формальных фраз типа "Создать петицию", "Прошу рассмотреть"\n- Без конкретных адресов, имен, цифр (но можно общую тему)\n\nПРИМЕРЫ ПРАВИЛЬНЫХ ЗАГОЛОВКОВ:\n- Текст: "В нашем районе перестали вывозить мусор. Жители требуют вернуть регулярный вывоз отходов."\n  → "Вернуть вывоз мусора в районе"\n  → "Регулярный вывоз мусора для жителей района"\n\n- Текст: "Нужно отремонтировать дорогу на улице Ленина"\n  → "Ремонт дороги на улице Ленина"\n  → "Отремонтировать дорогу"\n\n- Текст: "Против незаконной вырубки деревьев в парке"\n  → "Прекратить незаконную вырубку деревьев"\n  → "Запретить вырубку в парке"\n\nСоздай ЕСТЕСТВЕННЫЙ заголовок (до 8 слов) на основе ГЛАВНОЙ ИДЕИ из всего текста:`
      : `READ THE ENTIRE TEXT BELOW AND ANALYZE THE MAIN MESSAGE/IDEA THE AUTHOR WANTS TO CONVEY.\n\nText: "${text}"\n\nCRITICALLY IMPORTANT:\n1. IGNORE formal phrases at the beginning like "I want to create a petition about", "I need", "I request" - these are NOT part of the meaning\n2. Read the ENTIRE text completely, including the middle and end\n3. Determine the ESSENCE of the problem or request - what does the author really want?\n4. Create a title based on the ESSENCE, NOT on the first words\n\nExamples:\n- Text: "I want to create a petition about garbage collection on certain days of the week based on the city district"\n  Correct title: "Garbage Collection Schedule" (essence - organizing garbage collection by days)\n  WRONG: "I want to create a petition" (these are the first words, not the essence)\n\n- Text: "Please repair the road on Lenin Street, house 5, there are many potholes"\n  Correct title: "Road Repair" (essence - need repair)\n  WRONG: "Please repair the road" (these are the first words)\n\nTitle requirements:\n- 3-8 words, maximum 60 characters\n- Reflects the ESSENCE/MAIN IDEA of the problem, not formal words at the beginning\n- Without details, addresses, names, numbers\n- Examples of good titles: "Garbage Collection Schedule", "Road Repair", "New School", "Park Cleanup"\n\nCreate a title based on the ESSENCE (not first words!):`;

    const response = await fetch(`${this.ollamaBaseUrl}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: this.ollamaModel,
        prompt: prompt,
        stream: false,
        options: {
          temperature: 0.7,
          num_predict: 50
        }
      })
    });

    if (!response.ok) {
      throw new Error('Ошибка Ollama API');
    }

    const data = await response.json();
    let title = data.response.trim();
    
    // Очищаем заголовок от формальных фраз
    title = this.cleanTitleFromFormalPhrases(title, text);
    
    // Обрезаем до 60 символов, но по словам (не по середине слова)
    if (title.length > 60) {
      title = title.substring(0, 60).trim();
      const lastSpace = title.lastIndexOf(' ');
      if (lastSpace > 20) {
        title = title.substring(0, lastSpace).trim();
      }
    }
    
    return title;
  }

  async generatePetitionFromTranscript(transcript, language = 'ru', titleTranscript = '') {
    
    try {
      // Сначала исправляем грамматические ошибки в транскрипте
      const correctedTranscript = await this.fixGrammarErrors(transcript, language);
      
      if (this.provider === 'openai') {
        return await this.generateWithOpenAI(correctedTranscript, language, titleTranscript);
      } else if (this.provider === 'ollama') {
        return await this.generateWithOllama(correctedTranscript, language, titleTranscript);
      } else {
        throw new Error('Неизвестный AI провайдер');
      }
    } catch (error) {
      console.error('AI Service Error:', error);
      // Возвращаем базовую генерацию вместо ошибки
      return this.generateFallbackPetition(transcript, titleTranscript);
    }
  }

  generateFallbackPetition(transcript, titleTranscript = '') {
    
    // Генерируем заголовок на основе текста, если не предоставлен
    let title = titleTranscript.trim();
    if (!title) {
      // Используем cleanTitleFromFormalPhrases для извлечения сути
      let cleanedTranscript = transcript
        .replace(/^(так\s+)?(создай|создать)\s+петицию\s+(на\s+счёт|насчёт|о|расчёт|расчет|про)\s*/i, '')
        .replace(/^(я\s+)?(хочу|прошу|нужно)\s+(создать\s+)?петицию\s+(о|насчёт|расчёт|расчет|про|на\s+счёт)\s*/i, '')
        .trim();
      
      // Извлекаем ключевые фразы из всего текста, а не только начала
      const keyPhrases = [
        'поддержка пожилых людей',
        'социальные проекты для пожилых',
        'изготовление продукции',
        'вывоз мусора',
        'ремонт дороги',
        'строительство школы',
      ];
      
      let foundPhrase = '';
      for (const phrase of keyPhrases) {
        if (cleanedTranscript.toLowerCase().includes(phrase)) {
          foundPhrase = phrase;
          break;
        }
      }
      
      if (foundPhrase) {
        title = foundPhrase.charAt(0).toUpperCase() + foundPhrase.slice(1);
      } else {
        // Берем существительные из середины текста, игнорируя первые формальные слова
        const words = cleanedTranscript.split(/\s+/);
        const meaningfulWords = words.filter(w => w.length > 4).slice(0, 6);
        title = meaningfulWords.join(' ').substring(0, 60).trim();
      }
      
      if (!title) {
        title = 'Петиция';
      }
    }
    
    return {
      title: title,
      description: `Уважаемые представители власти!

Обращаюсь к вам с важным вопросом, который требует внимания и решения.

${transcript}

Прошу рассмотреть данное обращение и принять соответствующие меры.

С уважением,
Заявитель`
    };
  }

  async generateWithOpenAI(transcript, language, titleTranscript = '') {
    if (!this.openaiApiKey || this.openaiApiKey === 'your_openai_api_key_here') {
      throw new Error('OpenAI API ключ не настроен');
    }

    const prompt = this.createPetitionPrompt(transcript, language, titleTranscript);

    const response = await fetch(`${this.openaiBaseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.openaiApiKey}`
      },
      body: JSON.stringify({
        model: this.openaiModel,
        messages: [
          {
            role: 'system',
            content: 'Ты помощник для создания петиций. Создавай профессиональные, структурированные петиции на основе голосового ввода пользователя.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 1500
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Ошибка OpenAI API');
    }

    const data = await response.json();
    const generatedText = data.choices[0].message.content;

    return this.parseGeneratedPetition(generatedText, transcript);
  }

  async generateWithOllama(transcript, language, titleTranscript = '') {
    const prompt = this.createPetitionPrompt(transcript, language, titleTranscript);

    const response = await fetch(`${this.ollamaBaseUrl}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: this.ollamaModel,
        prompt: prompt,
        stream: false,
        options: {
          temperature: 0.7,
          num_predict: 1500
        }
      })
    });

    if (!response.ok) {
      throw new Error('Ошибка Ollama API');
    }

    const data = await response.json();
    return this.parseGeneratedPetition(data.response, transcript);
  }

  createPetitionPrompt(transcript, language, titleTranscript = '') {
    const langPrompts = {
      ru: `
ПРОЧИТАЙ ВЕСЬ ТЕКСТ НИЖЕ И ПРОАНАЛИЗИРУЙ ОСНОВНОЙ ПОСЫЛ/ИДЕЮ, КОТОРУЮ ХОЧЕТ ДОНЕСТИ АВТОР:

ОПИСАНИЕ ПРОБЛЕМЫ (ПРОЧТИ ВЕСЬ ТЕКСТ):
"${transcript}"

ИНСТРУКЦИЯ:
1. СНАЧАЛА внимательно прочитай и проанализируй ВЕСЬ текст описания проблемы выше
2. Определи ОСНОВНОЙ ПОСЫЛ/ИДЕЮ, которую хочет донести автор (какую главную мысль, проблему, просьбу)
3. На основе ОСНОВНОГО ПОСЫЛА создай заголовок оптимальной длины
4. Затем создай полное описание петиции на основе всего текста

Создай петицию в формате JSON с двумя полями:
1. title - Заголовок на основе основного посыла (оптимальная длина)
2. description - полное описание петиции в виде обычного текста

ТРЕБОВАНИЯ К ЗАГОЛОВКУ (КРИТИЧЕСКИ ВАЖНО!):
- СНАЧАЛА прочитай ВЕСЬ текст описания проблемы полностью (не только начало!)
- Определи СУТЬ/ОСНОВНУЮ ИДЕЮ проблемы или просьбы
- Заголовок должен быть ЕСТЕСТВЕННЫМ (до 8 слов), как на сайте с петициями
- БЕЗ фраз вроде "Создать петицию", "Мы требуем", "Прошу", "Петиция о..."
- Определи позицию автора: ЗА что-то или ПРОТИВ
- Если ПРОТИВ → используй: "Прекратить...", "Отменить...", "Запретить..."
- Если ЗА → используй: "Разрешить...", "Поддержать...", "Создать...", "Вернуть..."
- Оптимальная длина для понимания сути проблемы
- Отражает ТОЛЬКО суть проблемы, без деталей, адресов, имен, цифр
- Если предоставлен предварительный заголовок "${titleTranscript}", используй его как подсказку, но основной заголовок должен быть основан на СУТИ из всего текста
- Примеры правильных заголовков:
  * Текст: "В нашем районе перестали вывозить мусор. Жители требуют вернуть регулярный вывоз отходов." → "Вернуть вывоз мусора в районе"
  * Текст: "Нужно отремонтировать дорогу" → "Ремонт дороги"
  * Текст: "Против незаконной вырубки деревьев" → "Прекратить незаконную вырубку деревьев"

ТРЕБОВАНИЯ К ОПИСАНИЮ:
- Обычный читаемый текст на русском языке
- 2-4 абзаца
- Официальный, но понятный стиль
- Абзацы разделяй символами \\n\\n
- НЕ используй маркированные списки
- НЕ используй JSON объекты внутри description
- Только текст и переносы строк
- Адаптируй содержание под конкретную проблему из голосового сообщения
- В описание входит ВЕСЬ текст проблемы, детали, адреса, конкретная информация

ПРИМЕР ПРАВИЛЬНОГО ФОРМАТА:
{
  "title": "Ремонт дороги",
  "description": "Уважаемые представители муниципальных властей!\\n\\nОбращаемся к вам с просьбой о проведении капитального ремонта дорожного покрытия на улице Ленина, дом 5. Текущее состояние дороги создает угрозу безопасности движения и приводит к повреждению автомобилей. На дороге образовалось множество ям и трещин, что делает движение небезопасным.\\n\\nПросим рассмотреть данное обращение и принять необходимые меры по устранению проблемы.\\n\\nС уважением, жители города."
}

ОТВЕТЬ ТОЛЬКО ВАЛИДНЫМ JSON, БЕЗ ДОПОЛНИТЕЛЬНОГО ТЕКСТА.
      `,
      en: `
READ THE ENTIRE TEXT BELOW AND ANALYZE THE MAIN MESSAGE/IDEA THE AUTHOR WANTS TO CONVEY:

PROBLEM DESCRIPTION (READ ALL TEXT):
"${transcript}"

INSTRUCTION:
1. FIRST carefully read and analyze the ENTIRE problem description text above
2. Determine the MAIN MESSAGE/IDEA the author wants to convey (what main thought, problem, request)
3. Based on the MAIN MESSAGE, create a SHORT title (3-8 words, maximum 60 characters)
4. Then create the full petition description based on the entire text

Create a petition in JSON format with two fields:
1. title - SHORT title based on main message (3-8 words, maximum 60 characters)
2. description - full petition description as plain text

TITLE REQUIREMENTS (CRITICALLY IMPORTANT!):
- CRITICALLY IMPORTANT: IGNORE formal phrases at the beginning like "I want to create a petition about", "I need", "I request" - these are NOT part of the meaning, just formal words
- FIRST read the ENTIRE problem description text completely (not just the beginning!)
- Determine the ESSENCE/MAIN IDEA of the problem or request (what does the author really want? what is the real problem?)
- Title must reflect the ESSENCE of the problem, NOT the first words or formal phrases
- Title must be VERY SHORT: 3-8 words, maximum 60 characters
- Reflect ONLY the essence of the problem (e.g., "Garbage Collection Schedule", "Road Repair", "New School", "Transport Improvement")
- DO NOT include details, specific addresses, names, numbers - only the essence
- If a preliminary title "${titleTranscript}" is provided, use it as a hint, but the main title should be based on the ESSENCE from the entire text
- Examples of correct titles:
  * Text: "I want to create a petition about garbage collection on certain days of the week" → Title: "Garbage Collection Schedule"
  * Text: "Please repair the road on Lenin Street" → Title: "Road Repair"
  * Text: "Need to build a new school" → Title: "New School"
- Examples of incorrect titles:
  * Text: "I want to create a petition about garbage collection..." → WRONG: "I want to create a petition about garbage" (these are the first words)
  * Text: "Please repair the road..." → WRONG: "Please repair the road" (these are the first words)

DESCRIPTION REQUIREMENTS:
- Plain readable text in English
- 2-4 paragraphs
- Official but understandable style
- Separate paragraphs with \\n\\n
- DO NOT use bullet lists
- DO NOT use JSON objects inside description
- Only text and line breaks
- Adapt content to the specific problem from the voice message

EXAMPLE OF CORRECT FORMAT:
{
  "title": "Road Repair on Lenin Street",
  "description": "Dear Municipal Representatives!\\n\\nWe are addressing you with a request to conduct major repairs of the road surface on Lenin Street. The current condition of the road poses a threat to traffic safety and leads to vehicle damage.\\n\\nWe ask you to consider this appeal and take necessary measures to eliminate the problem.\\n\\nSincerely, city residents."
}

REPLY WITH VALID JSON ONLY, WITHOUT ADDITIONAL TEXT.
      `
    };

    return langPrompts[language] || langPrompts.ru;
  }

  /**
   * Очищает заголовок от формальных фраз и извлекает суть
   * @param {string} title - Заголовок для очистки
   * @param {string} originalTranscript - Исходный текст для извлечения сути (опционально)
   * @returns {string} - Очищенный заголовок
   */
  cleanTitleFromFormalPhrases(title, originalTranscript = '') {
    if (!title) return title;
    
    // Формальные фразы, которые нужно удалить
    const formalPhrases = [
      /^создать\s+петицию\s+/i,
      /^создай\s+петицию\s+/i,
      /^так\s+(создай|создать)\s+петицию\s+(на\s+счёт|насчёт|о|расчёт|расчет|про)\s+/i,
      /^так\s+создать\s+петицию\s+/i,
      /^так\s+петицию\s+/i,
      /^так\s+создай\s+петицию\s+(на\s+счёт|насчёт|о|расчёт|расчет|про)\s+/i,
      /^я\s+хочу\s+создать\s+петицию\s+насчёт\s*/i,
      /^хочу\s+создать\s+петицию\s+насчёт\s*/i,
      /^мне\s+нужно\s+создать\s+петицию\s+насчёт\s*/i,
      /^я\s+прошу\s+создать\s+петицию\s+насчёт\s*/i,
      /^прошу\s+создать\s+петицию\s+насчёт\s*/i,
      /^я\s+хочу\s+создать\s+петицию\s+о\s*/i,
      /^хочу\s+создать\s+петицию\s+о\s*/i,
      /^мне\s+нужно\s+создать\s+петицию\s+о\s*/i,
      /^я\s+прошу\s+создать\s+петицию\s+о\s*/i,
      /^прошу\s+создать\s+петицию\s+о\s*/i,
      /^я\s+хочу\s+петицию\s+насчёт\s*/i,
      /^хочу\s+петицию\s+насчёт\s*/i,
      /^мне\s+нужно\s+петицию\s+насчёт\s*/i,
      /^я\s+прошу\s+петицию\s+насчёт\s*/i,
      /^прошу\s+петицию\s+насчёт\s*/i,
      /^я\s+хочу\s+петицию\s+о\s*/i,
      /^хочу\s+петицию\s+о\s*/i,
      /^мне\s+нужно\s+петицию\s+о\s*/i,
      /^я\s+прошу\s+петицию\s+о\s*/i,
      /^прошу\s+петицию\s+о\s*/i,
      /^прошу\s+отремонтировать\s+/i,
      /^я\s+прошу\s+отремонтировать\s+/i,
      /^мне\s+нужно\s+отремонтировать\s+/i,
    ];
    
    let cleanedTitle = title.trim();
    
    // Пытаемся удалить формальные фразы
    for (const phrase of formalPhrases) {
      cleanedTitle = cleanedTitle.replace(phrase, '').trim();
    }
    
    // Дополнительная очистка: если заголовок все еще начинается с "на счёт" или "насчёт"
    cleanedTitle = cleanedTitle.replace(/^(на\s+счёт|насчёт)\s+/i, '').trim();
    
    // Если заголовок начинается с "счёт", но перед ним было "на", тоже удаляем
    if (cleanedTitle.toLowerCase().startsWith('счёт ')) {
      cleanedTitle = cleanedTitle.replace(/^счёт\s+/i, '').trim();
    }
    
    // Агрессивная очистка: удаляем все варианты "создать петицию" в начале заголовка
    cleanedTitle = cleanedTitle.replace(/^(создать|создай)\s+петицию\s+(против|за|о|насчёт|на\s+счёт)?\s*/i, '').trim();
    
    // Если заголовок начинается со слов "создать/создай петицию", удаляем их
    // НО "против" - это важное слово, его оставляем
    const titleWordsArray = cleanedTitle.split(/\s+/);
    let removeIndex = 0;
    for (let i = 0; i < Math.min(3, titleWordsArray.length); i++) {
      const word = titleWordsArray[i].toLowerCase();
      // Удаляем "создать", "создай", "петицию", "петиция"
      if (['создать', 'создай', 'петицию', 'петиция'].includes(word)) {
        removeIndex = i + 1;
      } else if (word === 'против' && i === 0) {
        // "против" в начале оставляем - это важное слово
        break;
      } else {
        break;
      }
    }
    if (removeIndex > 0) {
      cleanedTitle = titleWordsArray.slice(removeIndex).join(' ').trim();
    }
    
    // Если заголовок содержит несколько тем через "и", берем только первую тему
    if (cleanedTitle.toLowerCase().includes(' и ')) {
      const parts = cleanedTitle.split(/\s+и\s+/);
      if (parts.length > 1) {
        // Берем первую тему (до первого "и")
        cleanedTitle = parts[0].trim();
      }
    }
    
    // Финальная проверка: если заголовок все еще начинается с формальных слов
    if (cleanedTitle.toLowerCase().match(/^(создать|создай|петицию|петиция)\s+/)) {
      cleanedTitle = cleanedTitle.replace(/^(создать|создай|петицию|петиция)\s+/i, '').trim();
    }
    
    // Если заголовок все еще содержит формальные фразы или слишком похож на начало исходного текста
    if (originalTranscript && cleanedTitle.length > 0) {
      const transcriptStart = originalTranscript.toLowerCase().substring(0, Math.min(50, originalTranscript.length));
      const titleLower = cleanedTitle.toLowerCase();
      
      // Если заголовок слишком похож на начало исходного текста (больше 70% совпадения первых слов)
      // Но сначала удалим формальные слова из начала транскрипта для сравнения
      const cleanedTranscriptStart = transcriptStart
        .replace(/^(так\s+)?(создай|создать)\s+петицию\s+(на\s+счёт|насчёт|о|расчёт|расчет|про)\s*/i, '')
        .replace(/^(я\s+)?(хочу|прошу|нужно)\s+(создать\s+)?петицию\s+(о|насчёт|расчёт|расчет|про|на\s+счёт)\s*/i, '')
        .trim();
      
      const transcriptWords = cleanedTranscriptStart.split(/\s+/).slice(0, 6).filter(w => w.length > 2);
      const titleWords = titleLower.split(/\s+/).slice(0, 6);
      const matchingWords = titleWords.filter(word => transcriptWords.includes(word));
      
      // Проверяем, начинается ли заголовок с формальных слов
      const startsWithFormal = /^(так|я|мне|прошу|хочу|нужно|создай|создать|петицию|петиция|насчёт|на\s+счёт)/i.test(cleanedTitle);
      
      if ((matchingWords.length >= 3 && transcriptWords.length >= 3) || startsWithFormal) {
        // Заголовок похож на начало текста - попробуем извлечь суть программно
        // Берем ключевые слова из середины/конца текста
        const transcriptParts = originalTranscript.split(/[.,!?;]/);
        if (transcriptParts.length > 1) {
          // Берем вторую или третью часть (где обычно описывается суть)
          const essencePart = transcriptParts[1] || transcriptParts[0];
          const essenceWords = essencePart.trim().split(/\s+/).filter(word => 
            word.length > 3 && 
            !['про', 'что', 'как', 'где', 'когда', 'насчёт', 'относительно', 'касательно'].includes(word.toLowerCase())
          ).slice(0, 6);
          
          if (essenceWords.length >= 3) {
            cleanedTitle = essenceWords.join(' ').substring(0, 60).trim();
          }
        }
      }
    }
    
    // Если заголовок все еще начинается с формальных слов, обрезаем их
    const formalStartWords = ['так', 'я', 'мне', 'прошу', 'хочу', 'нужно', 'создать', 'создай', 'петицию', 'петиция', 'насчёт', 'на', 'счёт', 'относительно', 'расчёты', 'расчеты', 'против', 'и', 'или'];
    const titleWords = cleanedTitle.split(/\s+/);
    let formalWordIndex = 0;
    
    // Пропускаем формальные слова в начале
    for (let i = 0; i < titleWords.length && i < 6; i++) {
      const word = titleWords[i].toLowerCase();
      if (formalStartWords.includes(word)) {
        formalWordIndex = i + 1;
      } else if (word === 'петицию' || word === 'петиция') {
        // Если встретили слово "петицию" или "петиция", берем слова после него
        formalWordIndex = i + 1;
      } else if (word === 'счёт' && i > 0 && titleWords[i - 1].toLowerCase() === 'на') {
        // Если встретили "на счёт", пропускаем оба слова
        formalWordIndex = i + 1;
      } else {
        // Если встретили осмысленное слово, останавливаемся
        break;
      }
    }
    
    if (formalWordIndex > 0 && formalWordIndex < titleWords.length) {
      cleanedTitle = titleWords.slice(formalWordIndex).join(' ').trim();
    }
    
    // Дополнительная обработка: извлекаем ключевые фразы из всего текста
    if (originalTranscript && cleanedTitle.length > 0) {
      const transcriptLower = originalTranscript.toLowerCase();
      
      // Удаляем формальные слова из начала транскрипта для поиска сути
      let transcriptForEssence = transcriptLower
        .replace(/^так\s+(создай|создать)\s+петицию\s+(на\s+счёт|насчёт|о|расчёт|расчет|про)\s*/i, '')
        .replace(/^(так\s+)?(создать\s+)?петицию\s+(о|насчёт|расчёт|расчет|про|на\s+счёт)\s*/i, '')
        .replace(/^(я\s+)?(хочу|прошу|нужно)\s+(создать\s+)?петицию\s+(о|насчёт|расчёт|расчет|про|на\s+счёт)\s*/i, '')
        .replace(/^на\s+счёт\s+/i, '')
        .replace(/^насчёт\s+/i, '')
        .trim();
      
      // Ищем ключевые слова/темы в тексте (приоритет более конкретным и длинным фразам)
      // Порядок важен - более конкретные фразы первыми
      const keyPhrases = [
        'выделенная полоса для общественного транспорта',
        'выделенная полоса для транспорта',
        'выделенная полоса',
        'общественный транспорт',
        'экстренные оперативные службы',
        'экстренные службы',
        'разделение мусора',
        'разделение мусора по',
        'сортировка мусора по',
        'сортировка мусора',
        'вывоз мусора по',
        'вывоз мусора',
        'расчёт мусора',
        'расчет мусора',
        'ремонт дороги',
        'строительство школы',
        'очистка парка',
        'улучшение транспорта',
        'улучшение организации дорожного движения',
        'организация дорожного движения',
        'защита прав',
        'разделение',
        'сортировка',
        'мусор',
        'дорога',
        'школа',
        'парк',
        'транспорт'
      ];
      
      // Находим ключевую фразу в исходном тексте (приоритет длинным и более конкретным фразам)
      const foundPhrases = [];
      for (const phrase of keyPhrases.sort((a, b) => b.length - a.length)) {
        if (transcriptForEssence.includes(phrase)) {
          foundPhrases.push(phrase);
        }
      }
      
      // Используем самую длинную и конкретную найденную фразу
      if (foundPhrases.length > 0) {
        // Приоритет: более конкретные фразы (разделение, сортировка > расчёт, вывоз > просто мусор)
        const priorityPhrases = ['разделение', 'сортировка', 'вывоз', 'очистка', 'строительство', 'ремонт', 'улучшение'];
        let bestPhrase = foundPhrases[0];
        
        // Если есть фразы с приоритетными словами, выбираем их
        for (const phrase of foundPhrases) {
          if (priorityPhrases.some(priority => phrase.toLowerCase().includes(priority))) {
            bestPhrase = phrase;
            break;
          }
        }
        
        const phraseWords = bestPhrase.split(/\s+/);
        
        // Если фраза состоит из 2-5 слов - используем её
        if (phraseWords.length >= 2 && phraseWords.length <= 5) {
          // Обрезаем предлоги и детали в конце ("по", "по всему региону", "по алфавиту" и т.д.)
          let finalTitle = bestPhrase;
          // Удаляем предлоги и детали: "по", "по всему региону", "по алфавиту", "по всему", "всему региону"
          finalTitle = finalTitle.replace(/\s+по\s+(всему\s+)?(региону|району|городу|алфавиту|категориям|типам)$/i, '').trim();
          if (finalTitle.toLowerCase().endsWith(' по') && finalTitle.split(/\s+/).length > 2) {
            finalTitle = finalTitle.substring(0, finalTitle.length - 3).trim();
          }
          cleanedTitle = finalTitle.charAt(0).toUpperCase() + finalTitle.slice(1);
        } else if (phraseWords.length === 1) {
          // Если одно слово, пытаемся найти связанные слова рядом
          const phraseIndex = transcriptForEssence.indexOf(bestPhrase);
          if (phraseIndex >= 0) {
            const context = transcriptForEssence.substring(Math.max(0, phraseIndex - 20), phraseIndex + bestPhrase.length + 20);
            const contextWords = context.split(/\s+/).filter(w => w.length > 2);
            const phrasePos = contextWords.indexOf(bestPhrase);
            if (phrasePos >= 0 && phrasePos < contextWords.length - 1) {
              // Берем слово после ключевого
              const nextWord = contextWords[phrasePos + 1];
              if (nextWord && nextWord.length > 2 && !['по', 'для', 'из', 'на', 'в', 'к', 'от'].includes(nextWord.toLowerCase())) {
                cleanedTitle = (bestPhrase + ' ' + nextWord).charAt(0).toUpperCase() + (bestPhrase + ' ' + nextWord).slice(1);
              }
            }
          }
        }
      }
      
      // Дополнительная обработка: если в тексте несколько тем, берем первую
      if (transcriptForEssence.includes(' и ') && cleanedTitle.includes(' и ')) {
        const titleParts = cleanedTitle.split(/\s+и\s+/);
        if (titleParts.length > 1) {
          cleanedTitle = titleParts[0].trim();
        }
      }
      
      if (!foundPhrases.length) {
      // Если не нашли готовые фразы, извлекаем ключевые слова из текста
      // Но сначала разделим текст по "и" - если несколько тем, берем первую
      let textForExtraction = transcriptForEssence;
      if (transcriptForEssence.includes(' и ') && transcriptForEssence.split(' и ').length > 1) {
        // Берем только первую тему (до первого "и")
        textForExtraction = transcriptForEssence.split(/\s+и\s+/)[0].trim();
      }
      
      const stopWords = ['про', 'что', 'как', 'где', 'когда', 'насчёт', 'относительно', 'касательно', 'так', 'создать', 'петицию', 'петиция', 'по', 'для', 'из', 'на', 'в', 'к', 'от', 'алфавиту', 'алфавит', 'и', 'или', 'а', 'но'];
      const words = textForExtraction.split(/\s+/)
        .filter(word => {
          const wordLower = word.toLowerCase().trim();
          return wordLower.length > 2 && !stopWords.includes(wordLower);
        });
      
      // Ищем пары значимых слов (существительное + существительное/глагол)
      const meaningfulPairs = [];
      for (let i = 0; i < words.length - 1; i++) {
        const word1 = words[i].toLowerCase();
        const word2 = words[i + 1].toLowerCase();
        // Проверяем, что это значимые слова (не предлоги, не формальные слова)
        if (word1.length > 3 && word2.length > 3 && 
            !stopWords.includes(word1) && !stopWords.includes(word2)) {
          meaningfulPairs.push(words[i] + ' ' + words[i + 1]);
        }
      }
      
      if (meaningfulPairs.length > 0) {
        // Берем первую значимую пару
        cleanedTitle = meaningfulPairs[0].substring(0, 60).trim();
        cleanedTitle = cleanedTitle.charAt(0).toUpperCase() + cleanedTitle.slice(1);
      } else if (words.length >= 2) {
        // Если пар нет, берем первые 2-3 ключевых слова
        const keyWords = words.slice(0, 3);
        cleanedTitle = keyWords.join(' ').substring(0, 60).trim();
        cleanedTitle = cleanedTitle.charAt(0).toUpperCase() + cleanedTitle.slice(1);
      }
    }
    }
    
    // Не обрезаем заголовок - позволяем AI использовать необходимую длину
    
    return cleanedTitle || title; // Возвращаем оригинал, если очистка ничего не дала
  }

  parseGeneratedPetition(generatedText, originalTranscript = '') {
    try {
      
      // Удаляем markdown код блоки если есть
      let cleanText = generatedText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      
      // Пытаемся найти JSON в ответе
      const jsonMatch = cleanText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        
        // Проверяем что у нас есть title и description
        if (parsed.title && parsed.description) {
          let title = String(parsed.title).trim();
          let description = String(parsed.description).trim();
          
          // Очищаем заголовок от формальных фраз и извлекаем суть
          title = this.cleanTitleFromFormalPhrases(title, originalTranscript);
          
          // Не обрезаем заголовок - оставляем как есть
          
          // Убираем экранирование \n и заменяем на реальные переносы
          description = description.replace(/\\n/g, '\n');
          
          // Убираем лишние переносы строк (больше 2 подряд)
          description = description.replace(/\n{3,}/g, '\n\n');
          
          
          return {
            title: title,
            description: description
          };
        }
      }
      
      
      // Если JSON не найден, парсим как обычный текст
      const lines = generatedText.split('\n').filter(line => line.trim());
      let title = lines[0]?.substring(0, 60) || 'Петиция';
      
      // Очищаем заголовок от формальных фраз и извлекаем суть
      title = this.cleanTitleFromFormalPhrases(title, originalTranscript);
      
      // Не обрезаем заголовок - оставляем как сгенерировал AI
      return {
        title: title,
        description: lines.slice(1).join('\n\n') || generatedText
      };
    } catch (error) {
      console.error('Error parsing generated petition:', error);
      
      // В случае ошибки возвращаем весь текст как описание
      return {
        title: 'Петиция на основе голосового сообщения',
        description: generatedText
      };
    }
  }

  async moderateContent(content) {
    try {
      if (this.provider === 'openai') {
        return await this.moderateWithOpenAI(content);
      } else {
        // Для Ollama используем простую проверку по ключевым словам
        return this.basicModeration(content);
      }
    } catch (error) {
      console.error('Moderation Error:', error);
      return {
        is_safe: true,
        issues: []
      };
    }
  }

  async moderateWithOpenAI(content) {
    if (!this.openaiApiKey || this.openaiApiKey === 'your_openai_api_key_here') {
      return this.basicModeration(content);
    }

    const response = await fetch(`${this.openaiBaseUrl}/moderations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.openaiApiKey}`
      },
      body: JSON.stringify({
        input: content
      })
    });

    if (!response.ok) {
      return this.basicModeration(content);
    }

    const data = await response.json();
    const result = data.results[0];
    
    const issues = [];
    if (result.categories.hate) issues.push('Hate speech');
    if (result.categories.harassment) issues.push('Harassment');
    if (result.categories.violence) issues.push('Violence');
    if (result.categories['self-harm']) issues.push('Self-harm');
    if (result.categories.sexual) issues.push('Sexual content');

    return {
      is_safe: !result.flagged,
      issues: issues
    };
  }

  basicModeration(content) {
    const suspiciousWords = [
      'персональные данные', 'телефон', 'адрес', 'паспорт',
      'клевета', 'оскорбление', 'угроза'
    ];
    
    const issues = [];
    const lowerContent = content.toLowerCase();
    
    suspiciousWords.forEach(word => {
      if (lowerContent.includes(word)) {
        issues.push(`Обнаружено: ${word}`);
      }
    });

    return {
      is_safe: issues.length === 0,
      issues: issues
    };
  }
}

const aiService = new AIService();
export default aiService;